import { useSearchParams } from "react-router-dom";

interface IUseUrlTabOptions {
  /** The query parameter holding the tab key. Defaults to `tab`. */
  param?: string;
  /**
   * Whether a tab change keeps the page's other query params. Defaults to true. Eligibility
   * passes false on purpose: its other params are the filters of the tab being left.
   */
  keepOtherParams?: boolean;
}

interface IUseUrlTab {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
}

/**
 * A TabView's active tab, kept in the URL as `?tab=<key>` so each tab can be linked to.
 * A missing or unknown key opens the first tab. A tab change PUSHES a history entry, so Back
 * returns to the previous tab.
 */
export function useUrlTab(
  keys: readonly string[],
  { param = "tab", keepOtherParams = true }: IUseUrlTabOptions = {}
): IUseUrlTab {
  const [params, setParams] = useSearchParams();

  const activeIndex = Math.max(0, keys.indexOf(params.get(param) ?? ""));

  const setActiveIndex = (index: number) => {
    const next = new URLSearchParams(keepOtherParams ? params : undefined);
    next.set(param, keys[index]);
    setParams(next);
  };

  return { activeIndex, setActiveIndex };
}
